<template>
	<page-template>
		<template #content v-if="product">
			<v-container fluid>
				<v-row class="d-flex align-center justify-space-between flex-wrap px-4" no-gutters>
					<v-col cols="auto">
						<v-btn text outlined @click="$router.go(-1)">
							<v-icon left>icon-angle-left</v-icon>{{ $t("products") }}
						</v-btn>
					</v-col>
					<v-col cols="auto">
						<v-btn text outlined color="error" @click="deleteProduct">
							<v-icon left color="error">icon-delete</v-icon>{{ $t("delete") }}
						</v-btn>
					</v-col>
				</v-row>

				<v-row class="px-4">
					<v-col cols="12" lg="7">
						<product-details
							:product="product"
							:product-group-name="product.productGroupName"
							:lang="$lang"
							@edit="openProductDialog"
						/>
						<product-attributes
							:attributes="product.attributes"
							@add="openAttributeDialog(null)"
							@edit="openAttributeDialog"
							@delete="removeAttribute"
						/>
						<product-variants
							:variants="product.variants"
							:lang="$lang"
							:filtered-measurement-object="filteredMeasurementObject"
							@add="openVariantDialog(null)"
							@edit="openVariantDialog"
							@delete="removeVariant"
						/>
						<variant-dialog
							v-model="variantDialog.visible"
							:variant-data="variantDialog.editing"
							:unit-options="product.units"
							:all-units="product.allUnits"
							:product-name="product.name[$lang] || product.name._default"
							:product-code="product.code"
							:attribute-options="product.attributes"
							@save="saveVariant"
						/>
						<attribute-dialog
							v-model="attributeDialogVisible"
							:attribute-data="editingAttribute"
							:product-id="product._id"
							@save="onAttributeSaved"
						/>
					</v-col>

					<v-col cols="12" lg="5" class="pb-6">
						<product-description :description="product.description" :lang="$lang" @edit="openDescriptionDialog" />
						<product-images
							:product-id="product._id"
							:images="product.images"
							@refresh-product="loadProduct(product._id)"
						/>
					</v-col>
				</v-row>

				<product-dialog v-model="productDialogVisible" :item="product" @save="onProductSaved" />

				<description-dialog
					v-model="descriptionDialogVisible"
					:description-data="product.description"
					@save="saveDescription"
				/>
			</v-container>
		</template>
	</page-template>
</template>

<script>
import { mapGetters } from "vuex";
import uiMixin from "../../../store/interfaces/ui.mixin";
import PageTemplate from "../../templates/PageTemplate.vue";
import ProductDetails from "./partials/ProductDetails.vue";
import ProductAttributes from "./partials/ProductAttributes.vue";
import ProductVariants from "./partials/ProductVariants.vue";
import ProductDescription from "./partials/ProductDescription.vue";
import ProductDialog from "./dialog/ProductDialog.vue";
import AttributeDialog from "./dialog/ProductAttributesDialog.vue";
import DescriptionDialog from "./dialog/ProductDescriptionDialog.vue";
import VariantDialog from "./dialog/ProductVariantsDialog.vue";
import ProductImages from "./ProductImages.vue";
import remote from "../../../data/remote";

export default {
	mixins: [uiMixin],
	components: {
		PageTemplate,
		ProductDetails,
		ProductAttributes,
		ProductVariants,
		ProductDescription,
		ProductDialog,
		AttributeDialog,
		DescriptionDialog,
		VariantDialog,
		ProductImages,
	},
	data() {
		return {
			product: null,
			variantDialog: { visible: false, editing: null, index: null },
			attributeDialogVisible: false,
			descriptionDialogVisible: false,
			productDialogVisible: false,
			editingAttribute: null,
		};
	},
	computed: {
		...mapGetters(["companyDefaultLanguage", "companyLanguageOptions"]),
		$lang() {
			return this.$i18n.locale;
		},
	},
	methods: {
		loadProduct(id) {
			this.showProgressBar();
			remote.products.details({
				lang: this.$i18n.locale,
				productId: id,
				onSuccess: (r) => {
					this.product = r;
					this.hideProgressBar();
				},
				onFail: () => this.hideProgressBar(),
			});
		},
		filteredMeasurementObject(m) {
			const omit = new Set(["_id", "unit", "format", "formatId"]);
			return Object.fromEntries(
				Object.entries(m).filter(([k, v]) => v != null && !omit.has(k) && !k.toLowerCase().includes("gross"))
			);
		},
		openProductDialog() {
			this.productDialogVisible = true;
		},
		onProductSaved(item) {
			this.showProgressDialog();
			remote.products.update(item, {
				onSuccess: () => {
					this.loadProduct(item._id || item.id);
					this.hideProgressDialog();
				},
				onFail: () => this.hideProgressDialog(),
			});
		},
		openAttributeDialog(attr) {
			this.editingAttribute = attr ? { ...attr } : { productId: this.product._id };
			this.attributeDialogVisible = true;
		},
		onAttributeSaved() {
			this.loadProduct(this.product._id);
		},
		removeAttribute(id) {
			this.showConfirmDialog({
				message: this.$t("$message.confirm", { function: this.$t("toDelete") }),
				onConfirm: () => {
					this.showProgressDialog();
					remote.products.deleteAttribute({
						productId: this.product._id,
						attributeId: id,
						onSuccess: () => {
							this.loadProduct(this.product._id);
							this.hideProgressDialog();
						},
						onFail: () => this.hideProgressDialog(),
					});
				},
			});
		},
		openVariantDialog(v, i) {
			const editing = v
				? {
						...JSON.parse(JSON.stringify(v)),
						attributes: v.attributes.map((a) => ({
							...a,
							attributeId: a.attributeId.toString(),
							value: Array.isArray(a.value)
								? a.value.map((x) => x.toString())
								: a.value != null
								? [a.value.toString()]
								: [],
						})),
				  }
				: null;
			this.variantDialog = { visible: true, editing, index: i };
		},
		saveVariant(v) {
			this.showProgressDialog();
			const id = v._id || v.id;
			const method = id ? "updateVariant" : "addVariant";
			remote.products[method]({
				productId: this.product._id,
				variant: { ...v, _id: id },
				onSuccess: () => {
					this.loadProduct(this.product._id);
					this.hideProgressDialog();
				},
				onFail: () => this.hideProgressDialog(),
			});
		},
		removeVariant(i) {
			const vid = this.product.variants[i]._id;
			this.showConfirmDialog({
				message: this.$t("delete_confirm_message"),
				onConfirm: () => {
					this.showProgressDialog();
					remote.products.deleteVariant({
						productId: this.product._id,
						variantId: vid,
						onSuccess: () => {
							this.loadProduct(this.product._id);
							this.hideProgressDialog();
						},
						onFail: () => this.hideProgressDialog(),
					});
				},
			});
		},
		openDescriptionDialog() {
			this.descriptionDialogVisible = true;
		},
		saveDescription(desc) {
			this.showProgressDialog();
			remote.products.updateDescription(this.product._id, desc, {
				onSuccess: () => {
					this.loadProduct(this.product._id);
					this.hideProgressDialog();
					this.showSnackBar({ message: this.$t("$message.saved", { item: this.$t("description") }) });
				},
				onFail: () => this.hideProgressDialog(),
			});
		},
		deleteProduct() {
			this.showConfirmDialog({
				message: this.$t("$message.confirm", { function: this.$t("toDelete") }),
				onConfirm: () => {
					this.showProgressDialog();
					remote.products.delete(this.product._id, {
						onSuccess: () => {
							this.hideProgressDialog();
							this.$router.go(-1);
						},
						onFail: () => this.hideProgressDialog(),
					});
				},
			});
		},
	},
	created() {
		this.loadProduct(this.$route.params.id);
	},
};
</script>
